/**
 * Authentication Store
 * Manages user authentication state and session data using Zustand
 *
 * Features:
 * - Centralized auth state management
 * - Automatic persistence to localStorage
 * - Type-safe access to user data
 * - DevTools integration for debugging
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from 'types/auth.types';

/**
 * Auth store state and actions
 */
interface AuthStore {
  // State
  user: User | null;
  token: string | null;

  // Actions
  setUser: (user: User) => void;
  setToken: (token: string) => void;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  updateUser: (updates: Partial<User>) => void;

  // Getters (computed values)
  isAuthenticated: () => boolean;
  getUserId: () => string | null;
  getUser: () => User | null;
  getToken: () => string | null;
}

/**
 * Create auth store with persistence and devtools
 */
const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        token: null,

        // Set user data
        setUser: (user: User) => {
          set({ user }, false, 'setUser');
        },

        // Set authentication token
        setToken: (token: string) => {
          set({ token }, false, 'setToken');
        },

        // Set both user and token (convenience method)
        setAuth: (user: User, token: string) => {
          set({ user, token }, false, 'setAuth');
        },

        // Clear all authentication data
        clearAuth: () => {
          set({ user: null, token: null }, false, 'clearAuth');
        },

        // Update specific user fields
        updateUser: (updates: Partial<User>) => {
          const currentUser = get().user;
          if (currentUser) {
            set(
              { user: { ...currentUser, ...updates } },
              false,
              'updateUser'
            );
          }
        },

        // Check if user is authenticated
        isAuthenticated: () => {
          const state = get();
          return !!(state.token && state.user);
        },

        // Get user ID
        getUserId: () => {
          return get().user?.userId || null;
        },

        // Get user object
        getUser: () => {
          return get().user;
        },

        // Get token
        getToken: () => {
          return get().token;
        },
      }),
      {
        name: 'auth-storage', // localStorage key name
        // Only persist user and token
        partialize: (state) => ({
          user: state.user,
          token: state.token,
        }),
      }
    ),
    { name: 'AuthStore' } // DevTools name
  )
);

export default useAuthStore;
